import type { BlogPost } from '@viet-parenting/supabase/types';
import { pick, t } from '../i18n';
import { ReportLink } from './parts';
import type { DetailContext } from './types';

interface Props {
  post: BlogPost;
  ctx: DetailContext;
  /**
   * Where a tag chip links, given the tag. Returns null to render tags as
   * plain chips — the admin preview has no tag-filtered index to link to.
   */
  tagHref?: ((tag: string) => string) | null;
}

export default function BlogPostDetail({ post, ctx, tagHref = null }: Props) {
  const { locale, hrefs } = ctx;
  const title = pick(locale, post.title_vi, post.title_en);
  const body = pick(locale, post.body_vi ?? '', post.body_en);
  const pubDate = post.published_at
    ? new Date(post.published_at).toLocaleDateString(locale === 'vi' ? 'vi-VN' : 'en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '';

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <nav className="text-sm text-gray-400 mb-6">
        {hrefs.home && (
          <>
            <a href={hrefs.home} className="hover:text-red-700">
              Home
            </a>
            {' / '}
          </>
        )}
        <a href={hrefs.back} className="hover:text-red-700">
          {t(locale, 'blog')}
        </a>
        {' / '}
        <span className="text-gray-700">{title}</span>
      </nav>

      {post.cover_image && (
        <div className="rounded-xl overflow-hidden mb-8">
          <img
            src={ctx.imageUrl(post.cover_image, 'public')}
            alt={title}
            className="w-full h-64 object-cover"
          />
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        {post.tags.map((tag) => {
          const chipClass =
            'text-xs bg-red-50 text-red-700 font-semibold px-3 py-1 rounded-full';
          return tagHref ? (
            <a
              key={tag}
              href={tagHref(tag)}
              className={`${chipClass} hover:bg-red-100 transition-colors`}
            >
              {tag}
            </a>
          ) : (
            <span key={tag} className={chipClass}>
              {tag}
            </span>
          );
        })}
      </div>

      <h1 className="font-display text-4xl md:text-5xl font-extrabold leading-tight text-red-700 mb-4">
        {title}
      </h1>

      <div className="flex items-center gap-3 text-sm text-gray-500 mb-8 pb-8 border-b border-gray-100">
        <span className="font-medium text-gray-700">
          {locale === 'vi' ? 'Ban biên tập' : 'Editorial team'}
        </span>
        {pubDate && <span>·</span>}
        {pubDate && <span>{pubDate}</span>}
      </div>

      {body && (
        <div
          className="rich-content prose-sm max-w-none text-gray-700 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: body }}
        />
      )}

      <div className="mt-12 pt-8 border-t border-gray-100 flex items-center justify-between gap-4">
        <a href={hrefs.back} className="text-sm text-red-700 hover:underline">
          ← {locale === 'vi' ? 'Tất cả bài viết' : 'All posts'}
        </a>
        <ReportLink ctx={ctx} />
      </div>
    </div>
  );
}
